/**
 * TutorStudentProfileScreen — la fiche d'un élève, poussée : `student-profil`, et chaque route que
 * l'ancienne fiche poussait, ouverte sur son onglet (`tutorStudentProfileRoute.ts`). Un fil : la route,
 * l'onglet gardé ici, la composition de la fiche, « Générer une activité », la page et ses surfaces.
 *
 * Le retour : la pile (la fiche est poussée, A3) ; sans elle (un lien ouvert de l'extérieur), Mes élèves.
 */
import React, { useState } from "react"
import { View } from "react-native"

import { TutorStudentProfilePage } from "../../../../../../design-system/tutor"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorStudentTab } from "../../../types/tutorEleves"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { readTutorStudentProfileRoute, TAB_OF_ROUTE, type TutorStudentProfileRoute } from "./tutorStudentProfileRoute"
import { useTutorGenerateActivity } from "./useTutorGenerateActivity"
import { useTutorStudentProfile } from "./useTutorStudentProfile"

const BACK_TO_STUDENTS = { screen: "students" }

function TutorStudentProfileScreen(props: TutorSpaceScreenProps & { routeName: TutorStudentProfileRoute }) {
    const opening = readTutorStudentProfileRoute(props.routeName, props.route?.params as Record<string, unknown>)
    const [tab, setTab] = useState<UITutorStudentTab>(opening.tab)
    const onBack = useBackOr("menu", BACK_TO_STUDENTS)
    const generation = useTutorGenerateActivity()
    const { page, overlay } = useTutorStudentProfile({
        studentId: opening.studentId,
        tab,
        onChangeTab: setTab,
        visible: true,
        briefParams: opening.briefParams,
        onBack,
        openGeneration: generation.open,
    })

    return (
        <View style={{ flex: 1 }}>
            <TutorStudentProfilePage {...page} testID="tutor-student-profile" />
            {overlay}
            {generation.overlay}
        </View>
    )
}

const screenOn = (routeName: TutorStudentProfileRoute) => {
    const Screen = (props: TutorSpaceScreenProps) => <TutorStudentProfileScreen {...props} routeName={routeName} />
    Screen.displayName = `TutorStudentProfileScreen(${routeName})`
    return Screen
}

/** Un écran par route, gardé d'un rendu à l'autre : reconstruit, la fiche serait remontée. */
export const TUTOR_STUDENT_PROFILE_SCREENS = Object.fromEntries(
    (Object.keys(TAB_OF_ROUTE) as TutorStudentProfileRoute[]).map((route) => [route, screenOn(route)])
) as Record<TutorStudentProfileRoute, React.ComponentType<TutorSpaceScreenProps>>
